import React, {useContext, useRef, useState} from "react";
import AppContext from "../../appContext";
import LimeProgramLabInputRow from "../../controllers/LimeProgramLabInputRow";
import LimeProgramLabInputs from "../../controllers/LimeProgramLabInputs";
import {DataTable} from "../../components/DataTable";
import {PlusCircleIcon, ArrowUpOnSquareIcon} from "@heroicons/react/24/solid";
import {arrayPush, arrayRemoveIndex, arrayUpdate} from "../../immutableState";
import Dialog from "../../components/Dialog";
import FileUploader from "../../components/FileUploader";
import Papa from "papaparse";

interface LimeSoilDataDialogProps {
    data: LimeProgramLabInputs;
    onSave: (data: LimeProgramLabInputs) => void;
    onDiscard: () => void;
}

interface RowWithId extends LimeProgramLabInputRow {
    _id: number;
}

function emptyRow(id: number): RowWithId {
    return {
        _id: id,
        landRef: '',
        referenceNr: '',
        phH2O: 0,
        pBrayI: 0,
        k: 0,
        na: 0,
        ca: 0,
        mg: 0,
        exchH: 0,
        sulfur: 0,
        density: 1.2,
        caPercent: null,
        mgPercent: null,
        kPercent: null,
        naPercent: null,
        acidSat: null,
        sValue: null,
        cec: null,
        caMgRatio: null,
        caMgKRatio: null,
        mgKRatio: null,
        naKRatio: null
    }
}

let nextId = -1;
function getNextId(): number {
    return nextId--;
}

const LimeSoilDataDialog: React.FC<LimeSoilDataDialogProps> = (props) => {
    const app = useContext(AppContext);
    const [data, setData] = useState<RowWithId[]>(() =>
        props.data.rows.map((row, index) => ({...row, _id: index}))
    );
    const [showUpload, setShowUpload] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editRow, setEditRow] = useState<RowWithId>(emptyRow(0));
    const [editIndex, setEditIndex] = useState<number>(-1);
    const changed = useRef<boolean>(false);

    function handleCsvUpload(file: File) {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const rows: RowWithId[] = results.data.map((row: any) => ({
                    _id: getNextId(),
                    landRef: row['Land Ref'] || row['LandRef'] || row['Block'] || '',
                    referenceNr: row['Reference Nr'] || row['ReferenceNr'] || row['Ref'] || row['Sample#'] || '',
                    phH2O: parseFloat(row['pH(H2O)'] || row['pH_H2O'] || row['pH'] || '0') || 0,
                    pBrayI: parseFloat(row['P Bray I'] || row['P_BrayI'] || row['P'] || row['PBray'] || '0') || 0,
                    k: parseFloat(row['K'] || row['K mg/kg'] || row['K (mg/kg)'] || '0') || 0,
                    na: parseFloat(row['Na'] || row['Na mg/kg'] || row['Na (mg/kg)'] || '0') || 0,
                    ca: parseFloat(row['Ca'] || row['Ca mg/kg'] || row['Ca (mg/kg)'] || '0') || 0,
                    mg: parseFloat(row['Mg'] || row['Mg mg/kg'] || row['Mg (mg/kg)'] || '0') || 0,
                    exchH: parseFloat(row['Exch H'] || row['ExchH'] || row['H'] || row['OUT H+ (cmol(+)/kg)'] || '0') || 0,
                    sulfur: parseFloat(row['S'] || row['Sulfur'] || '0') || 0,
                    density: parseFloat(row['Density'] || row['BD'] || row['Density (g/cm3)'] || '1.2') || 1.2,
                    caPercent: parseFloat(row['Ca (%)'] || row['Ca%'] || '') || null,
                    mgPercent: parseFloat(row['Mg (%)'] || row['Mg%'] || '') || null,
                    kPercent: parseFloat(row['K (%)'] || row['K%'] || '') || null,
                    naPercent: parseFloat(row['Na (%)'] || row['Na%'] || '') || null,
                    acidSat: parseFloat(row['Acid Sat'] || row['AcidSat'] || row['SOUR.V (%)'] || '') || null,
                    sValue: parseFloat(row['S-Value'] || row['SValue'] || row['S-Value (cmol(+)/kg)'] || '') || null,
                    cec: parseFloat(row['CEC'] || row['T'] || row['T (cmol(+)/kg)'] || '') || null,
                    caMgRatio: parseFloat(row['Ca:Mg'] || row['CaMgRatio'] || row['Ca:Mg (1.5-4.5)'] || '') || null,
                    caMgKRatio: parseFloat(row['(Ca+Mg)/K'] || row['CaMgKRatio'] || row['(Ca+Mg)/K (10.0-20.0)'] || '') || null,
                    mgKRatio: parseFloat(row['Mg:K'] || row['MgKRatio'] || row['Mg:K (3.0-4.0)'] || '') || null,
                    naKRatio: parseFloat(row['Na:K'] || row['NaKRatio'] || '') || null
                }));

                setData(prev => [...prev, ...rows]);
                changed.current = true;
                setShowUpload(false);
            }
        });
    }

    function addRow() {
        setEditRow(emptyRow(getNextId()));
        setEditIndex(-1);
        setShowEdit(true);
    }

    function onEdit(row: RowWithId, index: number) {
        setEditRow({...row});
        setEditIndex(index);
        setShowEdit(true);
    }

    function onRemove(index: number) {
        setData(arrayRemoveIndex(data, index));
        changed.current = true;
    }

    function saveEdit() {
        if (editIndex === -1) {
            setData(arrayPush(data, editRow));
        } else {
            setData(arrayUpdate(data, editIndex, editRow));
        }
        changed.current = true;
        setShowEdit(false);
    }

    function formatValue(value: number | null): string {
        if (value === null) return '-';
        return value.toFixed(2);
    }

    return <>
        <Dialog
            title={app.word('upload_csv')}
            show={showUpload}
            setShow={setShowUpload}
            body={
                <div className="p-4">
                    <div>{app.word('download_csv_sample')}, {app.word('add_data_and_upload_again')}.</div>
                    <a className="underline" href="/LimeSoilSample.csv" download="LimeSoilSample.csv">LimeSoilSample.csv</a>
                    <div className="my-2">
                        <FileUploader onChange={file => handleCsvUpload(file)} fileTypes=".csv"/>
                    </div>
                </div>
            }
        />

        <Dialog
            title={editIndex === -1 ? app.word('add') : app.word('edit')}
            show={showEdit}
            setShow={setShowEdit}
            body={
                <div className="p-4 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-sm font-medium">{app.word('land_ref')}</label>
                            <input type="text" className="input w-full" value={editRow.landRef}
                                   onChange={e => setEditRow({...editRow, landRef: e.target.value})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{app.word('reference_nr')}</label>
                            <input type="text" className="input w-full" value={editRow.referenceNr}
                                   onChange={e => setEditRow({...editRow, referenceNr: e.target.value})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">pH(H2O)</label>
                            <input type="number" step="0.01" className="input w-full" value={editRow.phH2O}
                                   onChange={e => setEditRow({...editRow, phH2O: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">P Bray I</label>
                            <input type="number" step="0.1" className="input w-full" value={editRow.pBrayI}
                                   onChange={e => setEditRow({...editRow, pBrayI: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">K (mg/kg)</label>
                            <input type="number" step="0.1" className="input w-full" value={editRow.k}
                                   onChange={e => setEditRow({...editRow, k: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Na (mg/kg)</label>
                            <input type="number" step="0.1" className="input w-full" value={editRow.na}
                                   onChange={e => setEditRow({...editRow, na: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Ca (mg/kg)</label>
                            <input type="number" step="0.1" className="input w-full" value={editRow.ca}
                                   onChange={e => setEditRow({...editRow, ca: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Mg (mg/kg)</label>
                            <input type="number" step="0.1" className="input w-full" value={editRow.mg}
                                   onChange={e => setEditRow({...editRow, mg: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Exch H (cmol(+)/kg)</label>
                            <input type="number" step="0.01" className="input w-full" value={editRow.exchH}
                                   onChange={e => setEditRow({...editRow, exchH: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Sulfur</label>
                            <input type="number" step="0.01" className="input w-full" value={editRow.sulfur}
                                   onChange={e => setEditRow({...editRow, sulfur: parseFloat(e.target.value) || 0})}/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">Density (g/cm3)</label>
                            <input type="number" step="0.01" className="input w-full" value={editRow.density}
                                   onChange={e => setEditRow({...editRow, density: parseFloat(e.target.value) || 1.2})}/>
                        </div>
                    </div>
                    <div className="border-t mt-4 pt-4">
                        <div className="btn bg-red-500 mr-2" onClick={() => setShowEdit(false)}>{app.word('discard')}</div>
                        <div className="btn bg-primary" onClick={saveEdit}>{app.word('save')}</div>
                    </div>
                </div>
            }
        />

        <div className="my-2">
            {data.length > 0 ? (
                <>
                    <div className="text-xl text-primary mb-2">{app.word('soil_data')}</div>
                    <div className="overflow-x-auto">
                        <DataTable
                            headerClass="px-2 py-2 text-left text-xs font-semibold bg-gray-200"
                            dataClass="whitespace-nowrap px-1 py-2 text-xs text-gray-600"
                            keyExtractor={(row: RowWithId) => row._id}
                            definitions={[
                                {header: app.word('land_ref'), row: r => r.landRef},
                                {header: app.word('reference_nr'), row: r => r.referenceNr},
                                {header: 'pH(H2O)', row: r => formatValue(r.phH2O)},
                                {header: 'P Bray I', row: r => formatValue(r.pBrayI)},
                                {header: 'K (mg/kg)', row: r => formatValue(r.k)},
                                {header: 'Na (mg/kg)', row: r => formatValue(r.na)},
                                {header: 'Ca (mg/kg)', row: r => formatValue(r.ca)},
                                {header: 'Mg (mg/kg)', row: r => formatValue(r.mg)},
                                {header: 'Exch H', row: r => formatValue(r.exchH)},
                                {header: 'Ca (%)', row: r => formatValue(r.caPercent)},
                                {header: 'Mg (%)', row: r => formatValue(r.mgPercent)},
                                {header: 'K (%)', row: r => formatValue(r.kPercent)},
                                {header: 'Na (%)', row: r => formatValue(r.naPercent)},
                                {header: app.word('acid_saturated'), row: r => formatValue(r.acidSat)},
                                {header: 'Ca:Mg', row: r => formatValue(r.caMgRatio)},
                                {header: '(Ca+Mg)/K', row: r => formatValue(r.caMgKRatio)},
                                {header: 'Mg:K', row: r => formatValue(r.mgKRatio)},
                                {header: 'S-Value', row: r => formatValue(r.sValue)},
                                {header: 'Na:K', row: r => formatValue(r.naKRatio)},
                                {header: 'CEC', row: r => formatValue(r.cec)},
                                {header: 'Density', row: r => formatValue(r.density)},
                                {header: 'Sulfur', row: r => formatValue(r.sulfur)},
                                {
                                    header: app.word('actions'),
                                    row: (r, index) => <div>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onRemove(index)}>{app.word('remove')}</span>
                                        <span className="px-2">|</span>
                                        <span className="underline cursor-pointer text-primary"
                                              onClick={() => onEdit(r, index)}>{app.word('edit')}</span>
                                    </div>
                                }
                            ]}
                            data={data}
                        />
                    </div>
                </>
            ) : (
                <div>{app.word('no_soil_data')}</div>
            )}

            <div className="flex items-center mt-2">
                <span title={app.word('add')} className="mr-2">
                    <PlusCircleIcon className="w-6 h-6 cursor-pointer" onClick={addRow}/>
                </span>
                <span title={app.word('upload_csv')}>
                    <ArrowUpOnSquareIcon className="w-6 h-6 cursor-pointer" onClick={() => setShowUpload(true)}/>
                </span>
            </div>
        </div>

        <div className="border-t mt-4 pt-4">
            <div className="btn bg-red-500 mr-2" onClick={() => props.onDiscard()}>{app.word('discard')}</div>
            <div className="btn bg-primary" onClick={() => {
                if (changed.current) {
                    // Strip out the _id field before saving
                    const rowsWithoutId: LimeProgramLabInputRow[] = data.map(({_id, ...row}) => row);
                    props.onSave({rows: rowsWithoutId});
                } else {
                    props.onDiscard();
                }
            }}>{app.word('save')}</div>
        </div>
    </>;
};

export default LimeSoilDataDialog;
